import { useState } from 'react';
import { buildGrid, ABSENT, SOME, ALL } from '../../adoptionGrid';

/**
 * One school's adoptions as a Subject × Class grid.
 *
 * <p>This replaced a School × Title grid, and the reason is arithmetic: a title is a
 * subject and a class, so the catalogue can hold subjects × classes of them — 19 × 15
 * is 285, and no header row 285 wide is readable. On these axes the same catalogue is
 * at most 19 by 15, both axes are ordered, and both are things people say out loud.
 *
 * <p>It also lets the grid say something the old one could not. A cell with no title
 * is hatched rather than empty: there is no Class 9 Art, and a grid where "not taken"
 * and "does not exist" look identical is a grid that answers neither question.
 *
 * <p>Clicking a subject or a class takes or drops the whole line, because that is how
 * adoption is sold. Hovering one lights what it would change first.
 */
export default function SubjectClassGrid({ data, classOrder, busy, onApply }) {
  // The line the pointer is on, so a header click can show its reach before it lands.
  const [hover, setHover] = useState(null);
  const grid = buildGrid(data?.titles, classOrder);
  const editable = Boolean(data?.editable);

  if (grid.subjects.length === 0) {
    return <div className="empty">No titles in the catalogue yet.</div>;
  }

  const inLine = (subject, classLevel) =>
    (hover?.subject && hover.subject === subject) || (hover?.classLevel && hover.classLevel === classLevel);

  /** Takes a line unless it is already whole, which is what makes the click its own undo. */
  const apply = (line) => {
    if (!editable || busy || line.available === 0) return;
    onApply(line.titleIds, line.nextAdopted);
  };

  return (
    <div className="tbl-wrap">
      <table className="tbl mx">
        <thead>
          <tr>
            <th className="mx-corner" rowSpan={2}>
              <span className="tiny muted">Subject</span>
            </th>
            <th className="mx-axis" colSpan={grid.classes.length}>
              <span className="tiny muted">Class</span>
            </th>
          </tr>
          <tr>
            {grid.classes.map((classLevel) => {
              const column = grid.columnAt(classLevel);
              return (
                <th key={classLevel} className="mx-col">
                  <button
                    type="button"
                    className="mx-head-btn"
                    disabled={!editable || busy}
                    title={editable ? `Take or drop every ${classLevel} book` : classLevel}
                    onMouseEnter={() => setHover({ classLevel })}
                    onMouseLeave={() => setHover(null)}
                    onFocus={() => setHover({ classLevel })}
                    onBlur={() => setHover(null)}
                    onClick={() => apply(column)}
                  >
                    {/* "Class 8" repeated fifteen times across a header is the word
                        fifteen times; the axis above already said it. */}
                    {classLevel.replace(/^Class /, '')}
                    <span className="mx-count">
                      {column.adopted}/{column.available}
                    </span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {grid.subjects.map((subject) => {
            const row = grid.rowAt(subject);
            return (
              <tr key={subject}>
                <th className="mx-row">
                  <button
                    type="button"
                    className="mx-head-btn"
                    disabled={!editable || busy}
                    title={editable ? `Take or drop every ${subject} book` : subject}
                    onMouseEnter={() => setHover({ subject })}
                    onMouseLeave={() => setHover(null)}
                    onFocus={() => setHover({ subject })}
                    onBlur={() => setHover(null)}
                    onClick={() => apply(row)}
                  >
                    {subject}
                    <span className="mx-count">
                      {row.adopted}/{row.available}
                    </span>
                  </button>
                </th>

                {grid.classes.map((classLevel) => {
                  const cell = grid.cellAt(subject, classLevel);
                  const lit = inLine(subject, classLevel) ? ' in-line' : '';

                  if (cell.state === ABSENT) {
                    return (
                      <td
                        key={classLevel}
                        className={`mx-none${lit}`}
                        title={`No ${subject} book for ${classLevel}`}
                      >
                        <span aria-hidden="true">·</span>
                      </td>
                    );
                  }

                  const names = cell.titles.map((title) => title.name).join(', ');
                  return (
                    <td
                      key={classLevel}
                      className={`mx-cell${cell.state === ALL ? ' is-on' : ''}${lit}`}
                      title={names}
                    >
                      <label className="adopt">
                        <input
                          type="checkbox"
                          checked={cell.state === ALL}
                          disabled={!editable || busy}
                          aria-label={`${data.schoolName} adopts ${names}`}
                          onChange={() => apply(cell)}
                        />
                        {/* A half-taken cell is a dash, not a tick: a tick would be
                            claiming both books where only one is taken. */}
                        <span
                          className={`adopt-box${cell.state === SOME ? ' is-some' : ''}`}
                          aria-hidden="true"
                        />
                      </label>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>

        <tfoot>
          <tr>
            <th className="mx-row">
              <span className="tiny muted">Adopted</span>
            </th>
            {grid.classes.map((classLevel) => (
              <td key={classLevel}>{grid.columnAt(classLevel).adopted}</td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/** What the three cell states mean, said once rather than guessed at every cell. */
export function GridLegend() {
  return (
    <div className="legend">
      <span>
        <i className="key on" /> Adopted
      </span>
      <span>
        <i className="key" /> In the catalogue, not adopted
      </span>
      <span>
        <i className="key none" /> No such book
      </span>
    </div>
  );
}
